import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiClientWithApi } from '../../utils/http';
import type {
  BatchForm,
  FormTemplatesResponse,
  CreateFormTemplateRequest,
} from '../../models/TaskForm/TaskForm';
import type {
  TaskFormsRequest,
  TaskFormsResponse,
  TaskFormTemplatesRequest,
} from './TaskFormState';
import {
  buildSortQuery,
  repeatSerializer,
  buildTaskFormTemplatesRequestConfig,
  PAGED_PAGE_SIZE,
} from './TaskFormQuery';
export const getTaskForms = createAsyncThunk(
  'taskForm/getTaskForms',
  async (request: TaskFormsRequest, { rejectWithValue }) => {
    try {
      const { SortKeySelector, SortOrder, ...rest } = request;
      const params: Record<string, unknown> = {
        ...rest,
        sortQuery: buildSortQuery(SortKeySelector, SortOrder),
      };

      const response = await apiClientWithApi.get<TaskFormsResponse>(
        'task/form',
        {
          params,
          paramsSerializer: repeatSerializer,
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue(
        'Не удалось получить список заданий анкетирования!',
      );
    }
  },
);

export const getTaskFormTemplates = createAsyncThunk<
  FormTemplatesResponse,
  TaskFormTemplatesRequest | undefined,
  { rejectValue: string }
>('taskForm/getTemplates', async (request, { rejectWithValue }) => {
  try {
    const response = await apiClientWithApi.get<FormTemplatesResponse>(
      'task/form/template',
      buildTaskFormTemplatesRequestConfig(request),
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Не удалось получить шаблоны анкет!');
  }
});

export const getTaskFormManagementTemplates = createAsyncThunk<
  FormTemplatesResponse,
  TaskFormTemplatesRequest | undefined,
  { rejectValue: string }
>('taskForm/getManagementTemplates', async (request, { rejectWithValue }) => {
  try {
    const response = await apiClientWithApi.get<FormTemplatesResponse>(
      'task/form/template',
      buildTaskFormTemplatesRequestConfig(request),
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Не удалось получить шаблоны анкет!');
  }
});

export const getTaskFormBatches = createAsyncThunk(
  'taskForm/getBatches',
  async (_, { rejectWithValue }) => {
    try {
      const batches: BatchForm[] = [];
      let pageIndex = 0;
      let totalItemsCount = 0;
      do {
        const response = await apiClientWithApi.get<{
          items: BatchForm[];
          totalItemsCount: number;
        }>('task/form/batch', {
          params: {
            PageIndex: pageIndex,
            PageSize: PAGED_PAGE_SIZE,
            sortQuery: buildSortQuery('from', 'Desc'),
          },
          paramsSerializer: repeatSerializer,
        });
        const items = response.data.items ?? [];
        batches.push(...items);
        totalItemsCount = response.data.totalItemsCount;
        pageIndex += 1;
        if (items.length === 0) break;
      } while (batches.length < totalItemsCount);
      return batches;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue(
        'Не удалось получить список кампаний анкетирования!',
      );
    }
  },
);

export const createFormTemplate = createAsyncThunk<
  string,
  CreateFormTemplateRequest,
  { rejectValue: string }
>('taskForm/createTemplate', async (request, { rejectWithValue }) => {
  try {
    // Swagger documents POST /task/form/template as returning only the created UUID.
    const response = await apiClientWithApi.post<string>(
      'task/form/template',
      request,
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue((error.response?.data as string) ?? error.message);
    }
    return rejectWithValue('Не удалось создать шаблон анкеты!');
  }
});

export const deleteFormTemplate = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('taskForm/deleteTemplate', async (templateId, { rejectWithValue }) => {
  try {
    await apiClientWithApi.delete(`task/form/template/${templateId}`);
    return templateId;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue((error.response?.data as string) ?? error.message);
    }
    return rejectWithValue('Не удалось удалить шаблон анкеты!');
  }
});
